<template>
  <nav class="ScrollXNavbarCmp">
    <button
      v-if="
        dataNavigationEnabled &&
        (!disablePrev || dataNavigationAlwaysVisibleWhenEnabled)
      "
      type="button"
      class="button-prev"
      :disabled="disablePrev"
      @click.stop.prevent="onNav('prev')"
      @touchstart.stop.prevent="onNav('prev')"
    >
      <slot name="prev-icon"><span>&#171;</span></slot>
    </button>
    <ul ref="$el" :role="dataUlRole" @mousedown="mouseDownHandler">
      <slot />
    </ul>
    <button
      v-if="
        dataNavigationEnabled &&
        (!disableNext || dataNavigationAlwaysVisibleWhenEnabled)
      "
      type="button"
      class="button-next"
      :disabled="disableNext"
      @click.stop.prevent="onNav('next')"
      @touchstart.stop.prevent="onNav('next')"
    >
      <slot name="next-icon"><span>&#187;</span></slot>
    </button>
  </nav>
</template>

<script>
import { useWindowSize } from '@vueuse/core';

export default {
  name: 'ScrollXNavbarCmp',
  props: {
    dataActiveTabId: {
      type: String,
      required: true,
    },
    dataNavigationEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * dataNavigationAlwaysVisibleWhenEnabled
     * if dataNavigationEnabled is true by default the buttons are visible even if they have the attr disabled set to true,
     * you can set this prop to false if you want that the buttons are not visible if they have the attr disabled set to true
     */
    dataNavigationAlwaysVisibleWhenEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * dataSlotReady
     * needed only for async data slotted in the default slot
     */
    dataSlotReady: {
      type: null, // any type
      default: true,
    },
    dataUlRole: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { width: vw, height: vh } = useWindowSize();
    return { vw, vh };
  },
  data() {
    return {
      isCalculatingWidth: false,
      width: 0,
      widths: [],
      pos: {
        left: 0,
        x: 0,
      },
    };
  },
  computed: {
    disablePrev() {
      return this.widths.findIndex((e) => e.id === this.dataActiveTabId) === 0;
    },
    disableNext() {
      return (
        this.widths.findIndex((e) => e.id === this.dataActiveTabId) ===
        this.widths.length - 1
      );
    },
    shouldCheckOverflowX() {
      const _ = [this.isCalculatingWidth, this.width];
      return !!this.$refs.$el && (!this.isCalculatingWidth || this.width);
    },
    posLeft() {
      return this.pos.left;
    },
  },
  watch: {
    dataSlotReady: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.getWidth();
          });
        }
      },
      immediate: true,
    },
    shouldCheckOverflowX: {
      handler(val) {
        if (val) {
          const isOverflowX =
            this.width > this.$refs.$el.clientWidth || this.width > this.vw;
          this.$emit('is-overflow-x', isOverflowX);
        }
      },
      immediate: true,
    },
    posLeft: {
      handler(val) {
        this.$emit('is-scroll-left', val > 0);
      },
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
  },
  methods: {
    getWidth() {
      this.isCalculatingWidth = true;
      this.$nextTick(() => {
        this.$refs.$el.querySelectorAll('li').forEach((e, idx) => {
          this.widths[idx] = { id: e.id, width: e.clientWidth };
        });
        this.width = this.$refs.$el.scrollWidth;
        this.isCalculatingWidth = false;
      });
    },
    resizeHandler() {
      this.getWidth();
    },
    isVisible(ele, container) {
      const eleRectX = ele.getBoundingClientRect().x;
      const eleRectWidth = ele.clientWidth;
      const containerRectX = container.getBoundingClientRect().x;
      const containerRectWidth = container.clientWidth;

      return (
        Math.round(eleRectX) >= Math.round(containerRectX) &&
        Math.round(eleRectX + eleRectWidth) <=
          Math.round(containerRectX + containerRectWidth)
      );
    },
    getScrollAmount(ele, container) {
      const eleRectX = ele.getBoundingClientRect().x;
      const eleRectWidth = ele.clientWidth;
      const containerRectX = container.getBoundingClientRect().x;
      const containerRectWidth = container.clientWidth;

      return Math.round(eleRectX) >= Math.round(containerRectX)
        ? Math.round(eleRectX + eleRectWidth) -
            Math.round(containerRectX + containerRectWidth)
        : Math.round(eleRectX) - Math.round(containerRectX);
    },
    mouseDownHandler(e) {
      const tgt = e.target.classList.contains('tab')
        ? e.target
        : e.target.closest('.tab');

      if (tgt && !this.isVisible(tgt, this.$refs.$el)) {
        const amount = this.getScrollAmount(tgt, this.$refs.$el);
        this.$refs.$el.scrollLeft = this.$refs.$el.scrollLeft + amount;
      }

      if (this.width > this.$refs.$el.clientWidth || this.width > this.vw) {
        // Change the cursor and prevent user from selecting the text
        this.$refs.$el.classList.add('grab');

        // The current scroll
        this.pos.left = this.$refs.$el.scrollLeft;
        // Get the current mouse position
        this.pos.x = e.clientX;

        window.addEventListener('mousemove', this.mouseMoveHandler);
        window.addEventListener('mouseup', this.mouseUpHandler);
      }
    },
    mouseMoveHandler(e) {
      this.$refs.$el.classList.add('grabbing');

      // How far the mouse has been moved
      const dx = e.clientX - this.pos.x;

      // Scroll the element
      this.$refs.$el.scrollLeft = this.pos.left - dx;
      this.$emit('is-scroll-left', this.$refs.$el.scrollLeft > 0);
    },
    mouseUpHandler(e) {
      // Reset
      this.$refs.$el.classList.remove('grab');
      this.$refs.$el.classList.remove('grabbing');

      // The current scroll
      this.pos.left = this.$refs.$el.scrollLeft;
      // Get the current mouse position
      this.pos.x = e.clientX;

      window.removeEventListener('mousemove', this.mouseMoveHandler);
      window.removeEventListener('mouseup', this.mouseUpHandler);
    },
    onNav(direction) {
      let accWidth = 0;
      let idx = -1;
      this.widths.some((e, index) => {
        accWidth += e.width;
        if (e.id === this.dataActiveTabId) {
          idx = index;

          return true;
        }

        return false;
      });
      if (idx !== -1) {
        if (direction === 'prev') {
          if (idx > 0 && idx < this.widths.length) {
            this.$refs.$el.scrollLeft =
              accWidth - this.widths[idx].width - this.widths[idx - 1].width;
            // The current scroll left
            this.pos.left =
              accWidth - this.widths[idx].width - this.widths[idx - 1].width;
            this.$emit('navigation', this.widths[idx - 1].id);
          }
        }
        if (direction === 'next') {
          if (idx < this.widths.length - 1) {
            this.$refs.$el.scrollLeft = accWidth;
            // The current scroll left
            this.pos.left = accWidth;
            this.$emit('navigation', this.widths[idx + 1].id);
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ScrollXNavbarCmp {
  position: relative;
  width: var(--scroll-x-navbar-width, 100%);
  max-width: var(--scroll-x-navbar-max-width, none);
  overflow-y: hidden;
  padding-inline: var(--scroll-x-navbar-spacing-x);

  .button-prev,
  .button-next {
    display: flex;
    z-index: var(--scroll-x-navbar-button-z-index, 1);
    position: absolute;
    top: 50%;
    left: var(
      --scroll-x-navbar-button-spacing-x,
      var(--scroll-x-navbar-spacing-x, 0px)
    );
    align-items: center;
    justify-content: center;
    width: var(--scroll-x-navbar-button-width);
    height: var(--scroll-x-navbar-button-height);
    transform: translateY(-50%);
    border: var(--scroll-x-navbar-button-border-width)
      var(--scroll-x-navbar-button-border-style)
      var(--scroll-x-navbar-button-border-color);
    border-radius: var(--scroll-x-navbar-button-border-radius);
    background: var(--scroll-x-navbar-button-bg-color);
    box-shadow: var(--scroll-x-navbar-button-box-shadow);
    color: var(--scroll-x-navbar-button-color);
    cursor: pointer;
    user-select: none;

    ::v-deep *:first-child {
      flex-shrink: 0;
      display: block;
      width: var(--scroll-x-navbar-button-icon-width, 1em);
      height: var(--scroll-x-navbar-button-icon-height, 1em);
      font-size: var(--scroll-x-navbar-button-icon-font-size, 1rem);
      line-height: 1;
      text-align: center;
      color: inherit;
    }

    &:disabled {
      border: var(
          --scroll-x-navbar-button-disabled-border-width,
          var(--scroll-x-navbar-button-border-width)
        )
        var(
          --scroll-x-navbar-button-disabled-border-style,
          var(--scroll-x-navbar-button-border-style)
        )
        var(
          --scroll-x-navbar-button-disabled-border-color,
          var(--scroll-x-navbar-button-border-color)
        );
      background: var(
        --scroll-x-navbar-button-disabled-bg-color,
        var(--scroll-x-navbar-button-bg-color)
      );
      box-shadow: var(
        --scroll-x-navbar-button-disabled-box-shadow,
        var(--scroll-x-navbar-button-box-shadow)
      );
      color: var(
        --scroll-x-navbar-button-disabled-color,
        var(--scroll-x-navbar-button-color)
      );
      cursor: not-allowed;
    }
  }

  .button-next {
    right: var(
      --scroll-x-navbar-button-spacing-x,
      var(--scroll-x-navbar-spacing-x, 0px)
    );
    left: auto;
  }

  &::after {
    position: absolute;
    right: var(
      --scroll-x-navbar-border-bottom-spacing-x,
      var(--scroll-x-navbar-spacing-x)
    );
    bottom: 0;
    left: var(
      --scroll-x-navbar-border-bottom-spacing-x,
      var(--scroll-x-navbar-spacing-x)
    );
    border-bottom: var(--scroll-x-navbar-border-bottom-width, 1px)
      var(--scroll-x-navbar-border-bottom-style, solid)
      var(--scroll-x-navbar-border-bottom-color);
    content: var(
      --scroll-x-navbar-border-bottom-content,
      ''
    ); // set to none if you want to remove the border
  }

  ul {
    -ms-overflow-style: none;
    display: flex;
    align-items: flex-start;
    column-gap: var(--scroll-x-navbar-ul-column-gap);
    margin: 0;
    padding: var(--scroll-x-navbar-ul-padding);
    background-color: var(--scroll-x-navbar-ul-bg-color);
    overflow-x: auto;
    list-style-type: none;
    white-space: nowrap;
    isolation: isolate;
    scrollbar-width: none;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }

    ::v-deep li.tab {
      flex-shrink: 0;
      background-color: var(--scroll-x-navbar-tab-bg-color);
      color: var(--scroll-x-navbar-tab-color);
      border-radius: var(--scroll-x-navbar-tab-border-radius);

      &.active {
        background-color: var(
          --scroll-x-navbar-tab-active-bg-color,
          var(--scroll-x-navbar-tab-bg-color)
        );
        color: var(
          --scroll-x-navbar-tab-active-color,
          var(--scroll-x-navbar-tab-color)
        );
        --scroll-x-navbar-tab-cursor: default;
      }

      &:hover:not(.active) {
        background-color: var(
          --scroll-x-navbar-tab-hover-bg-color,
          var(--scroll-x-navbar-tab-bg-color)
        );
        color: var(
          --scroll-x-navbar-tab-hover-color,
          var(--scroll-x-navbar-tab-color)
        );
      }

      .tab-label {
        display: block;
        padding: var(--scroll-x-navbar-tab-padding);
        color: inherit;
        text-decoration: inherit;
        cursor: var(--scroll-x-navbar-tab-cursor, pointer);
      }
    }

    &.grab {
      cursor: grab;
      user-select: none;

      ::v-deep li.tab {
        --scroll-x-navbar-tab-cursor: grab;
        user-select: none;
      }
    }

    &.grabbing {
      cursor: grabbing;

      ::v-deep li.tab {
        --scroll-x-navbar-tab-cursor: grabbing;
      }
    }
  }
}
</style>
